/**
 * scores.ts — KPI / Wheel-of-Life / Fulfillment / Confidence scores.
 *
 * DAT-114 (Data Validation Rules):
 *   - KPI scores (Energy/Work/Love): 1-10 integer
 *   - Identity Gap score: 1-10 integer
 *   - Wheel of Life scores: 1-10 integer
 *   - Fulfillment Score: 0-100 integer (auto-calculated)
 *   - Confidence score in reframing: 1-10 integer
 *
 * Server rejects out-of-range with 422; client surfaces inline errors.
 */

import { z } from 'zod';

const oneToTenInt = z.number().int().min(1).max(10);

export const KpiScore = oneToTenInt.describe('Daily KPI score (1-10).');
export type KpiScore = z.infer<typeof KpiScore>;

export const IdentityGapScore = oneToTenInt.describe(
  'Identity gap baseline / final score (1-10).',
);
export type IdentityGapScore = z.infer<typeof IdentityGapScore>;

export const VirtueScore = oneToTenInt.describe(
  'Self-assessed virtue score (1-10).',
);
export type VirtueScore = z.infer<typeof VirtueScore>;

export const WolScore = oneToTenInt.describe(
  'Wheel-of-Life dimension score (1-10).',
);
export type WolScore = z.infer<typeof WolScore>;

export const ConfidenceScore = oneToTenInt.describe(
  'Six-Step-Reframing confidence score (1-10).',
);
export type ConfidenceScore = z.infer<typeof ConfidenceScore>;

/**
 * Auto-calculated by the server from weighted activity completion (CbR
 * + AM/PM bookends count 2x; ONE Thing / confidence log / virtue
 * affirmations / masterpiece day count 1x). Never client-submitted.
 */
export const FulfillmentScore = z
  .number()
  .int()
  .min(0)
  .max(100)
  .describe('Fulfillment Score (0-100, auto-calculated).');
export type FulfillmentScore = z.infer<typeof FulfillmentScore>;
